/**
 * Non-component helpers for the organisation pages (kept apart from orgUi.tsx
 * so that file exports only components — React fast refresh needs that).
 */

import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { T } from '@/components/shell/orgTheme'
import { orgApi, type OrgList, type OrgRole } from '@/lib/orgApi'
import { useOrgs } from '@/lib/useOrgs'

export const inputStyle: CSSProperties = {
  padding: '9px 12px', border: `1px solid ${T.inputBorder}`, borderRadius: 8, background: '#fff', fontSize: 14, outline: 'none', color: T.dark,
}
export const focusRing = 'focus:border-[#7A3BD9] focus:shadow-[0_0_0_3px_#EFE6FC]'

export type Status = 'Active' | 'Invited' | 'Deactivated' | 'Expired'

export interface ConfirmSpec {
  title: string; body: ReactNode; cta: string; danger?: boolean; run: () => Promise<void> | void
  /** The dismiss button's label — 'Cancel' (21 Oct 2026; was 'Keep as is') unless a dialog says otherwise. */
  cancelLabel?: string
  /** What the dismiss button does besides closing (e.g. "Keep going" carries on with the course). */
  onCancel?: () => void
  /** A second way out, between dismiss and the main action (e.g. Deactivate instead of Remove). */
  alt?: { label: string; run: () => Promise<void> | void }
  /** The main button while it works, when its own verb in -ing doesn't fit (orgUi progressive). */
  busyLabel?: string
}

export const ROLE_LABEL: Record<OrgRole, string> = { owner: 'Owner', admin: 'Admin', creator: 'Creator', learner: 'Learner' }
export const rolesText = (roles: readonly OrgRole[]) => roles.map(r => ROLE_LABEL[r]).join(', ')
/** "an Owner", "a Learner" — the highest role they hold, for a sentence. */
export const mainRoleText = (roles: readonly OrgRole[] | null | undefined) => {
  const r = (['owner', 'admin', 'creator', 'learner'] as const).find(x => roles?.includes(x)) ?? 'learner'
  return `${r === 'owner' || r === 'admin' ? 'an' : 'a'} ${ROLE_LABEL[r]}`
}

// ─── Data ────────────────────────────────────────────────────────────────────

/** The current organisation's members, invites and settings (owner / admin only). */
// The last list per organisation (7 Oct 2026): Members and Invite people show it at once and
// refresh it quietly, instead of a blank page for the second a fresh load takes.
const listCache = new Map<string, OrgList>()

export function useOrgList() {
  const { current, isManager } = useOrgs()
  // Kept with the org it belongs to, so switching organisation never shows the previous one's list.
  const [state, setState] = useState<{ orgId: string; data: OrgList | null; error: string | null } | null>(null)
  const orgId = current?.id ?? null

  const reload = useCallback(async () => {
    if (!orgId || !isManager) return
    try { const data = await orgApi.list(orgId); listCache.set(orgId, data); setState({ orgId, data, error: null }) }
    catch (e) { setState(s => ({ orgId, data: s?.orgId === orgId ? s.data : listCache.get(orgId) ?? null, error: (e as Error).message })) }
  }, [orgId, isManager])

  useEffect(() => { void reload() }, [reload])
  const mine = state?.orgId === orgId ? state : null
  return { orgId, data: mine?.data ?? (orgId ? listCache.get(orgId) ?? null : null), error: mine?.error ?? null, reload }
}

/** For tests: forget the remembered lists. */
export function resetOrgListCache() { listCache.clear() }

export function when(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const thisYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: thisYear ? undefined : 'numeric' })
}

export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true } catch { return false }
}


/** Below 768px the org pages switch tables for cards. */
export function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < 768)
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return narrow
}

/** Buttons on the public join cards. */
export const joinBtnPrimary = { padding: '11px 16px', borderRadius: 9, border: 'none', background: T.purple, color: '#fff', fontSize: 14.5, fontWeight: 600, cursor: 'pointer' } as const
export const joinBtnSecondary = { padding: '10px 16px', borderRadius: 9, border: `1px solid ${T.inputBorder}`, background: '#fff', fontSize: 14.5, fontWeight: 500, cursor: 'pointer', color: T.dark } as const

/** Where an organisation code link points (public route /o/:code, OrgCodeLinkPage). */
export const codeJoinLink = (code: string) => `${window.location.origin}/o/${code}`

/** "just now", "5 minutes ago", "2 days ago", then a date. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return ''
  const s = (Date.now() - Date.parse(iso)) / 1000
  if (s < 60) return 'just now'
  const [n, unit] = s < 3600 ? [Math.floor(s / 60), 'minute'] : s < 86400 ? [Math.floor(s / 3600), 'hour'] : [Math.floor(s / 86400), 'day']
  if (unit === 'day' && n > 13) return `on ${when(iso)}`
  return `${n} ${unit}${n === 1 ? '' : 's'} ago`
}
